/* eslint-disable no-multi-spaces, newline-per-chained-call */
import { Box3, Object3D, Quaternion, Vector3 } from 'three';

/**
 * Represents a face of a bounding box
 */
export interface BoundingBoxFace {
	/** Center point of the face in world coordinates */
	point: Vector3;
	/** Axis this face is perpendicular to */
	axis: 'x' | 'y' | 'z';
	/** Direction along axis (1 = positive, -1 = negative) */
	direction: 1 | -1;
	/** Normal vector of the face */
	normal: Vector3;
}

/**
 * Represents a face with its alignment score
 */
export interface FaceWithAlignment extends BoundingBoxFace {
	/** Dot product alignment with the reference direction */
	alignment: number;
}

/**
 * Labeled faces of an object's bounding box
 */
export interface LabeledFaces {
	front: FaceWithAlignment;
	back: FaceWithAlignment;
	left: FaceWithAlignment;
	right: FaceWithAlignment;
	top: FaceWithAlignment;
	bottom: FaceWithAlignment;
}

/**
 * Get all 6 faces of an object's axis-aligned bounding box
 */
export const getBoundingBoxFaces = (object: Object3D): BoundingBoxFace[] => {
	const boundingBox = new Box3().setFromObject(object);
	const objectCenter = new Vector3();
	const size = new Vector3();

	boundingBox.getCenter(objectCenter);
	boundingBox.getSize(size);
	const halfSize = size.clone().multiplyScalar(0.5);

	return [
		// +Z face
		{
			point: new Vector3(objectCenter.x, objectCenter.y, objectCenter.z + halfSize.z),
			axis: 'z',
			direction: 1,
			normal: new Vector3(0, 0, 1)
		},
		// -Z face
		{
			point: new Vector3(objectCenter.x, objectCenter.y, objectCenter.z - halfSize.z),
			axis: 'z',
			direction: -1,
			normal: new Vector3(0, 0, -1)
		},
		// +X face
		{
			point: new Vector3(objectCenter.x + halfSize.x, objectCenter.y, objectCenter.z),
			axis: 'x',
			direction: 1,
			normal: new Vector3(1, 0, 0)
		},
		// -X face
		{
			point: new Vector3(objectCenter.x - halfSize.x, objectCenter.y, objectCenter.z),
			axis: 'x',
			direction: -1,
			normal: new Vector3(-1, 0, 0)
		},
		// +Y face (top)
		{
			point: new Vector3(objectCenter.x, objectCenter.y + halfSize.y, objectCenter.z),
			axis: 'y',
			direction: 1,
			normal: new Vector3(0, 1, 0)
		},
		// -Y face (bottom)
		{
			point: new Vector3(objectCenter.x, objectCenter.y - halfSize.y, objectCenter.z),
			axis: 'y',
			direction: -1,
			normal: new Vector3(0, -1, 0)
		}
	];
};

/**
 * Determine labeled faces based on object's world rotation
 *
 * Uses the object's world quaternion to determine which AABB faces
 * correspond to front, back, left, right, top, and bottom.
 *
 * - front: The horizontal face most aligned with the object's forward direction
 * - back: The horizontal face opposite to front
 * - left/right: The other two horizontal faces
 * - top: The face most aligned with the object's up direction
 * - bottom: The face opposite to top
 */
export const getLabeledFaces = (object: Object3D): LabeledFaces => {
	const faces = getBoundingBoxFaces(object);

	// Get object's world rotation
	const worldQuaternion = new Quaternion();
	object.getWorldQuaternion(worldQuaternion);

	// Transform local forward (0, 0, 1) to world space
	const localForward = new Vector3(0, 0, 1);
	const worldForward = localForward.clone().applyQuaternion(worldQuaternion)
		.normalize();

	// Transform local up (0, 1, 0) to world space
	const localUp = new Vector3(0, 1, 0);
	const worldUp = localUp.clone().applyQuaternion(worldQuaternion)
		.normalize();

	// Separate horizontal (X/Z) and vertical (Y) faces
	const horizontalFaces = faces.filter((face) => face.axis !== 'y');
	const verticalFaces = faces.filter((face) => face.axis === 'y');

	// Calculate alignment with forward direction for horizontal faces
	const horizontalWithAlignment: FaceWithAlignment[] = horizontalFaces.map((face) => ({
		...face,
		alignment: face.normal.dot(worldForward)
	}));

	// Sort by forward alignment (descending)
	horizontalWithAlignment.sort((a, b) => b.alignment - a.alignment);

	// Calculate alignment with up direction for vertical faces
	const verticalWithAlignment: FaceWithAlignment[] = verticalFaces.map((face) => ({
		...face,
		alignment: face.normal.dot(worldUp)
	}));

	// Sort by up alignment (descending)
	verticalWithAlignment.sort((a, b) => b.alignment - a.alignment);

	return {
		front: horizontalWithAlignment[0], // Best aligned with forward
		back: horizontalWithAlignment[horizontalWithAlignment.length - 1], // Worst aligned
		left: horizontalWithAlignment[1], // One perpendicular face
		right: horizontalWithAlignment[2], // Other perpendicular face
		top: verticalWithAlignment[0], // Best aligned with up
		bottom: verticalWithAlignment[1] // Opposite
	};
};

/**
 * Check if an object is a "top only" type (countertop or top-only appliance)
 */
export const isTopOnlyObject = (object: Object3D): boolean => {
	const userData = object.userData || {};
	const objectType: string = typeof userData.type === 'string' ? userData.type : '';
	const applianceSubtype: string | null = typeof userData.subtype === 'string' ? userData.subtype : null;
	const normalizedSubtype = applianceSubtype?.toLowerCase() ?? '';

	return objectType === 'countertop' ||
		(objectType === 'appliance' && (normalizedSubtype === 'top only' || normalizedSubtype === 'top'));
};

/**
 * Get the highlight target face based on object type
 *
 * - Top-only objects (countertops, etc.): Use top face
 * - Everything else: Use front face
 */
export const getHighlightTargetFace = (object: Object3D): FaceWithAlignment => {
	const faces = getLabeledFaces(object);
	return isTopOnlyObject(object) ? faces.top : faces.front;
};

/**
 * Calculate object center from bounding box
 */
export const getObjectCenter = (object: Object3D): Vector3 => {
	const boundingBox = new Box3().setFromObject(object);
	const center = new Vector3();
	boundingBox.getCenter(center);
	return center;
};

